/**
 * PendingInvites — /pending-invites
 * Shows pending workspace invitations for a logged-in user.
 * Accept or reject each invitation.
 */

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@shared/lib/supabaseClient';
import { Button } from '@shared/ui/shadcn/ui/button';
import { Alert, AlertDescription } from '@shared/ui/shadcn/ui/alert';
import { Loader2, LogOut, Check, X } from 'lucide-react';
import AuthLayout from '../components/AuthLayout';

interface PendingInvite {
  id: string;
  empresa_id: string;
  empresa_nombre: string;
  rol: string;
  invited_by_name: string;
  created_at: string;
  token: string;
}

const PendingInvitesPage: React.FC = () => {
  const navigate = useNavigate();
  const [invites, setInvites] = useState<PendingInvite[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [processingId, setProcessingId] = useState<string | null>(null);
  const [userName, setUserName] = useState('');
  const [userInitials, setUserInitials] = useState('');

  useEffect(() => {
    loadInvites();
  }, []);

  async function loadInvites() {
    setLoading(true);
    setError(null);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        navigate('/login', { replace: true });
        return;
      }

      // Get user display name
      const { data: appUser } = await supabase
        .from('app_users')
        .select('full_name')
        .eq('user_id', user.id)
        .maybeSingle();

      const name = appUser?.full_name || user.user_metadata?.full_name || user.email?.split('@')[0] || '';
      setUserName(name);
      setUserInitials(
        name
          .split(' ')
          .filter(Boolean)
          .map((w: string) => w[0])
          .join('')
          .slice(0, 2)
          .toUpperCase() || '?'
      );

      // Fetch pending invites for this user's email/phone
      const userEmail = user.email || '';
      const userPhone = user.phone || '';

      const { data: links, error: linksErr } = await supabase
        .from('empresa_invite_links')
        .select('id, token, empresa_id, payload, expires_at, revoked_at, created_at')
        .is('revoked_at', null)
        .gt('expires_at', new Date().toISOString());

      if (linksErr) {
        setError('Error cargando invitaciones.');
        setLoading(false);
        return;
      }

      // Filter invites that match this user's email or phone
      const matching = (links || []).filter((link: any) => {
        const payload = link.payload || {};
        const invEmail = (payload.email || '').toLowerCase();
        const invPhone = (payload.phone || '').replace(/\D/g, '');
        const uPhone = userPhone.replace(/\D/g, '');
        return (
          (invEmail && invEmail === userEmail.toLowerCase()) ||
          (invPhone && uPhone && invPhone.slice(-10) === uPhone.slice(-10))
        );
      });

      // Enrich with empresa names
      const empresaIds = [...new Set(matching.map((m: any) => m.empresa_id))];
      let empresaMap: Record<string, string> = {};
      if (empresaIds.length > 0) {
        const { data: empresas } = await supabase
          .from('empresas')
          .select('id, nombre')
          .in('id', empresaIds);
        if (empresas) {
          empresaMap = Object.fromEntries(empresas.map((e: any) => [e.id, e.nombre]));
        }
      }

      const enriched: PendingInvite[] = matching.map((link: any) => ({
        id: link.id,
        empresa_id: link.empresa_id,
        empresa_nombre: empresaMap[link.empresa_id] || 'Workspace',
        rol: link.payload?.rol || link.payload?.rol_codigo || 'Miembro',
        invited_by_name: link.payload?.invited_by_name || 'Admin',
        created_at: link.created_at,
        token: link.token,
      }));

      setInvites(enriched);
    } catch (err: any) {
      setError(err.message || 'Error inesperado.');
    } finally {
      setLoading(false);
    }
  }

  async function handleAccept(invite: PendingInvite) {
    setProcessingId(invite.id);
    setError(null);
    try {
      // Redirect to the unirse flow which handles all the assignment logic
      navigate(`/empresa/unirse?token=${encodeURIComponent(invite.token)}`);
    } catch (err: any) {
      setError(err.message || 'Error al aceptar la invitacion.');
      setProcessingId(null);
    }
  }

  async function handleReject(invite: PendingInvite) {
    setProcessingId(invite.id);
    setError(null);
    try {
      // Mark invite as revoked for this user
      await supabase
        .from('empresa_invite_links')
        .update({ revoked_at: new Date().toISOString() })
        .eq('id', invite.id);

      setInvites((prev) => prev.filter((i) => i.id !== invite.id));
    } catch (err: any) {
      setError(err.message || 'Error al rechazar la invitacion.');
    } finally {
      setProcessingId(null);
    }
  }

  async function handleSignOut() {
    await supabase.auth.signOut();
    navigate('/login', { replace: true });
  }

  function getWorkspaceInitials(name: string): string {
    return name
      .split(' ')
      .slice(0, 2)
      .map((w) => w[0])
      .join('')
      .toUpperCase();
  }

  if (loading) {
    return (
      <AuthLayout>
        <div className="flex flex-col items-center justify-center gap-4 py-8">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
          <p className="text-sm text-muted-foreground">Cargando invitaciones...</p>
        </div>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout>
      <div className="w-full flex flex-col gap-4">
        {/* User info header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2.5 text-sm">
            <div className="w-6 h-6 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-[10px] font-bold font-mono">
              {userInitials}
            </div>
            <span className="text-foreground">{userName}</span>
          </div>
          <button
            onClick={handleSignOut}
            className="text-xs text-primary hover:underline font-medium"
          >
            Salir
          </button>
        </div>

        {/* Title */}
        <div className="text-center space-y-1">
          <h1 className="text-xl font-semibold text-foreground">
            {invites.length > 0
              ? `Tienes ${invites.length} invitacion${invites.length > 1 ? 'es' : ''} pendiente${invites.length > 1 ? 's' : ''}.`
              : 'No tienes invitaciones pendientes.'}
          </h1>
          {invites.length > 0 && (
            <p className="text-sm text-muted-foreground">
              Revisa los workspaces que te invitaron a unirte. Puedes aceptar uno o varios — los datos no se mezclan entre ellos.
            </p>
          )}
        </div>

        {error && (
          <Alert variant="destructive">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        {/* Invite cards */}
        {invites.length > 0 && (
          <div className="space-y-3">
            {invites.map((invite) => (
              <div key={invite.id}>
                {/* Invite card */}
                <div className="flex items-center gap-3 p-3 rounded-lg border border-border bg-card">
                  <div
                    className="w-10 h-10 rounded-md flex items-center justify-center text-white font-mono font-bold text-sm flex-shrink-0"
                    style={{ background: 'hsl(var(--primary))' }}
                  >
                    {getWorkspaceInitials(invite.empresa_nombre)}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold text-sm text-foreground truncate">
                      {invite.empresa_nombre}
                    </div>
                    <div className="text-xs text-muted-foreground">{invite.rol}</div>
                  </div>
                  <div className="text-right text-xs text-muted-foreground flex-shrink-0">
                    <div>Invitado por</div>
                    <div className="font-semibold text-foreground">{invite.invited_by_name}</div>
                  </div>
                </div>

                {/* Action buttons */}
                <div className="flex gap-2 mt-1.5">
                  <Button
                    className="flex-1"
                    size="sm"
                    disabled={processingId === invite.id}
                    onClick={() => handleAccept(invite)}
                  >
                    {processingId === invite.id ? (
                      <Loader2 className="h-4 w-4 animate-spin mr-1" />
                    ) : (
                      <Check className="h-4 w-4 mr-1" />
                    )}
                    Aceptar
                  </Button>
                  <Button
                    className="flex-1"
                    variant="outline"
                    size="sm"
                    disabled={processingId === invite.id}
                    onClick={() => handleReject(invite)}
                  >
                    <X className="h-4 w-4 mr-1" />
                    Rechazar
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}

        {invites.length === 0 && (
          <div className="text-center py-4">
            <Button variant="outline" onClick={() => navigate('/login?app=empresa')}>
              Ir al inicio de sesion
            </Button>
          </div>
        )}

        {/* Footer */}
        <p className="text-center text-xs text-muted-foreground mt-2">
          Las invitaciones expiran 7 dias despues de enviadas.
        </p>
      </div>
    </AuthLayout>
  );
};

export default PendingInvitesPage;
